import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { useBranding } from "@/lib/branding";

const CATEGORIES = [
  { id: "security",       label: "Security" },
  { id: "collaboration",  label: "Collaboration" },
  { id: "productivity",   label: "Productivity" },
  { id: "local_ai",       label: "Local AI / Copilots" },
  { id: "developer",      label: "Developer" },
  { id: "creative",       label: "Creative / Media AI" },
  { id: "sales_field",    label: "Sales / Field" },
  { id: "industry",       label: "Energy / OT" },
];

export default function SubmitSolutionDialog({ open, onOpenChange, onSubmitted }) {
  const { userName, userEmail } = usePersona();
  const { brand } = useBranding();
  const [form, setForm] = useState({
    name: "", vendor: "", category: "productivity", description: "", personas: [],
    tops_required: 0, license: "Enterprise seat", on_device: true,
  });
  const [saving, setSaving] = useState(false);
  const catLabel = CATEGORIES.find((c) => c.id === form.category)?.label || "Productivity";

  const togglePersona = (p) => {
    const set = new Set(form.personas);
    if (set.has(p)) set.delete(p); else set.add(p);
    setForm({ ...form, personas: [...set] });
  };

  const submit = async () => {
    if (!form.name || !form.vendor || !form.description) {
      toast.error("Name, vendor and description are required");
      return;
    }
    setSaving(true);
    try {
      const doc = await endpoints.createSubmission({
        ...form,
        category_label: catLabel,
        submitted_by: userEmail || "anon@flexspace.demo",
        submitter_org: userName || brand.orgName,
      });
      toast.success(`Submission received · #${doc.id.slice(0, 8)}`);
      onSubmitted?.(doc);
      onOpenChange(false);
      setForm({ ...form, name: "", vendor: "", description: "", personas: [] });
    } catch (e) {
      toast.error("Submission failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white" data-testid="submit-solution-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-lg">Submit a new AI solution</DialogTitle>
          <DialogDescription className="text-xs">
            OEM partners, internal developers and vendors can publish new apps or bundles here. Every submission goes through security review, IT Admin approval and only then lands in the catalog.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Solution name *</Label>
              <Input data-testid="submit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Baker Hughes Reservoir AI" className="mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Vendor / Publisher *</Label>
              <Input data-testid="submit-vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="e.g. Baker Hughes" className="mt-1" />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Description *</Label>
            <Textarea data-testid="submit-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What does it do, who is it for, how does it use the NPU/GPU?" rows={3} className="mt-1" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Category</Label>
              <select
                data-testid="submit-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1 w-full border border-slate-200 rounded-md h-9 px-2 text-sm bg-white"
              >
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">TOPS required</Label>
              <Input data-testid="submit-tops" type="number" value={form.tops_required} onChange={(e) => setForm({ ...form, tops_required: parseInt(e.target.value || "0") })} className="mt-1" />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Validated for personas</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {["developer", "designer", "executive", "sales", "field_engineer"].map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`submit-persona-${p}`}
                  onClick={() => togglePersona(p)}
                  className={`text-[11px] px-2.5 py-1 rounded-md border capitalize transition-colors ${
                    form.personas.includes(p)
                      ? "bg-[#0033A0] text-white border-[#0033A0]"
                      : "border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {p.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">License model</Label>
              <Input data-testid="submit-license" value={form.license} onChange={(e) => setForm({ ...form, license: e.target.value })} className="mt-1" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  data-testid="submit-on-device"
                  type="checkbox"
                  checked={form.on_device}
                  onChange={(e) => setForm({ ...form, on_device: e.target.checked })}
                  className="rounded border-slate-300"
                />
                Runs on-device (NPU / local runtime)
              </label>
            </div>
          </div>

          <div className="border border-slate-200 bg-slate-50 rounded-md p-3 text-[11px] text-slate-600">
            After submission, the solution enters a 4-stage pipeline:
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {["Received", "Security review", "IT Admin approval", "Published"].map((s, i) => (
                <Badge key={s} variant="outline" className="text-[10px] border-slate-300 bg-white text-slate-700">{i + 1}. {s}</Badge>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="submit-solution-btn" disabled={saving} onClick={submit} className="bg-[#0033A0] hover:bg-[#002573] text-white">
            <Send size={13} className="mr-1.5" />
            {saving ? "Submitting…" : "Submit for review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
